<script setup lang="ts">
import { useForm } from "@inertiajs/vue3"
import { CsrfField } from "@/components/csrf-field"
import { Button } from "@/components/ui/button"
import { Input } from "@/components/ui/input"
import { Label } from "@/components/ui/label"
import AuthLayout from "@/layouts/AuthLayout.vue"

defineOptions({
    layout: [AuthLayout],
})

type Props = {
    links: {
        store_path: string
    }
}

const { links } = defineProps<Props>()

const form = useForm({
    name: "",
    email: "",
    mobile: "",
    address: "",
    birthday: "",
    date_started_at: new Date().toISOString().slice(0, 10),
    rate_per_hour: "",
    full_shift_rate: "",
    password: "",
    password_confirmation: "",
})

const fieldClass =
    "h-11 border-[#c9a56a]/25 bg-[#0f0b07]/60 text-[#f7f1e8] placeholder:text-[#d8c7ae]/40 focus-visible:ring-[rgb(181_115_5)]"

function numericOnly(value: string) {
    const cleaned = value.replace(/[^\d.]/g, "")
    const [whole = "", ...decimals] = cleaned.split(".")

    if (decimals.length === 0) {
        return whole
    }

    return `${whole}.${decimals.join("").slice(0, 2)}`
}

function onRateInput(field: "rate_per_hour" | "full_shift_rate", event: Event) {
    const input = event.target as HTMLInputElement
    form[field] = numericOnly(input.value)
}

function submit() {
    form.post(links.store_path)
}
</script>

<template>
    <div class="grid gap-5">
        <div class="space-y-1">
            <h2 class="font-recipe text-2xl font-bold tracking-tight text-[#f4e6d0]">
                Staff registration
            </h2>
            <p class="text-sm text-[#d8c7ae]/75">
                Create your profile to start the onboarding manual and exam.
            </p>
        </div>

        <form class="max-h-[min(68dvh,640px)] space-y-3 overflow-y-auto pr-1" method="POST" @submit.prevent="submit">
            <CsrfField />

            <div class="grid gap-1.5">
                <Label for="name" class="text-[#e8dcc8]">Name</Label>
                <Input id="name" v-model="form.name" type="text" required :class="fieldClass" />
                <p v-if="form.errors.name" class="text-sm text-red-400">{{ form.errors.name }}</p>
            </div>

            <div class="grid gap-1.5">
                <Label for="email" class="text-[#e8dcc8]">Email</Label>
                <Input id="email" v-model="form.email" type="email" required :class="fieldClass" />
                <p v-if="form.errors.email" class="text-sm text-red-400">{{ form.errors.email }}</p>
            </div>

            <div class="grid gap-1.5">
                <Label for="mobile" class="text-[#e8dcc8]">Mobile</Label>
                <Input id="mobile" v-model="form.mobile" type="text" :class="fieldClass" />
                <p v-if="form.errors.mobile" class="text-sm text-red-400">{{ form.errors.mobile }}</p>
            </div>

            <div class="grid gap-1.5">
                <Label for="address" class="text-[#e8dcc8]">Address</Label>
                <Input id="address" v-model="form.address" type="text" required :class="fieldClass" />
                <p v-if="form.errors.address" class="text-sm text-red-400">{{ form.errors.address }}</p>
            </div>

            <div class="grid grid-cols-2 gap-2">
                <div class="grid gap-1.5">
                    <Label for="birthday" class="text-[#e8dcc8]">Birthday</Label>
                    <Input id="birthday" v-model="form.birthday" type="date" required :class="fieldClass" />
                    <p v-if="form.errors.birthday" class="text-sm text-red-400">{{ form.errors.birthday }}</p>
                </div>
                <div class="grid gap-1.5">
                    <Label for="date_started_at" class="text-[#e8dcc8]">Start date</Label>
                    <Input id="date_started_at" v-model="form.date_started_at" type="date" required :class="fieldClass" />
                    <p v-if="form.errors.date_started_at" class="text-sm text-red-400">{{ form.errors.date_started_at }}</p>
                </div>
            </div>

            <div class="grid grid-cols-2 gap-2">
                <div class="grid gap-1.5">
                    <Label for="rate_per_hour" class="text-[#e8dcc8]">Hourly rate</Label>
                    <Input
                        id="rate_per_hour"
                        v-model="form.rate_per_hour"
                        type="text"
                        inputmode="decimal"
                        pattern="[0-9.]*"
                        autocomplete="off"
                        required
                        :class="fieldClass"
                        @input="onRateInput('rate_per_hour', $event)"
                    />
                    <p v-if="form.errors.rate_per_hour" class="text-sm text-red-400">{{ form.errors.rate_per_hour }}</p>
                </div>
                <div class="grid gap-1.5">
                    <Label for="full_shift_rate" class="text-[#e8dcc8]">Full shift rate</Label>
                    <Input
                        id="full_shift_rate"
                        v-model="form.full_shift_rate"
                        type="text"
                        inputmode="decimal"
                        pattern="[0-9.]*"
                        autocomplete="off"
                        required
                        :class="fieldClass"
                        @input="onRateInput('full_shift_rate', $event)"
                    />
                    <p v-if="form.errors.full_shift_rate" class="text-sm text-red-400">{{ form.errors.full_shift_rate }}</p>
                </div>
            </div>

            <div class="grid gap-1.5">
                <Label for="password" class="text-[#e8dcc8]">Password</Label>
                <Input id="password" v-model="form.password" type="password" required :class="fieldClass" />
                <p v-if="form.errors.password" class="text-sm text-red-400">{{ form.errors.password }}</p>
            </div>

            <div class="grid gap-1.5">
                <Label for="password_confirmation" class="text-[#e8dcc8]">Confirm password</Label>
                <Input id="password_confirmation" v-model="form.password_confirmation" type="password" required :class="fieldClass" />
            </div>

            <Button
                type="submit"
                :disabled="form.processing"
                class="mt-2 h-11 w-full bg-[rgb(181_115_5)] text-[#140e09] hover:bg-[rgb(212_160_70)]"
            >
                {{ form.processing ? "Creating account…" : "Register and start onboarding" }}
            </Button>
        </form>
    </div>
</template>
